import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { CookieCategory, cookieManager } from "~/lib/cookie-manager";
import { CookiePreferenceCenter } from "./CookiePreferenceCenter";

interface CookieConsentBannerProps {
  onAccept?: () => void;
  onDecline?: () => void;
}

export function CookieConsentBanner({ onAccept, onDecline }: CookieConsentBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if consent has been set
    const hasConsent = cookieManager.hasConsentBeenSet();

    // Use functional updates to avoid direct state setting warnings
    setShowBanner(() => !hasConsent);
    setIsLoading(() => false);
  }, []);

  const handleAcceptAll = () => {
    setIsLoading(true);

    // Accept all cookie categories
    cookieManager.setConsent({
      analytics: true,
      marketing: true,
    });

    // Smooth disappear
    setTimeout(() => {
      setShowBanner(false);
      setIsLoading(false);
      onAccept?.();
    }, 300);
  };

  const handleDeclineAll = () => {
    setIsLoading(true);

    // Decline all non-essential cookies
    cookieManager.setConsent({
      analytics: false,
      marketing: false,
    });

    // Smooth disappear
    setTimeout(() => {
      setShowBanner(false);
      setIsLoading(false);
      onDecline?.();
    }, 300);
  };

  const handleCustomizePreferences = () => {
    setShowPreferences(true);
  };

  const handlePreferencesSaved = () => {
    // Smooth disappear after preferences saved
    setTimeout(() => {
      setShowBanner(false);
      onAccept?.();
    }, 300);
  };

  // Don't render anything if loading or banner shouldn't be shown
  if (isLoading || !showBanner) {
    return (
      <>
        <CookiePreferenceCenter
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          onSave={handlePreferencesSaved}
        />
      </>
    );
  }

  return (
    <>
      {/* Subtle Cookie Consent Banner */}
      <div
        className={`fixed right-0 bottom-0 left-0 z-50 border-t border-gray-700/30 bg-black/90 backdrop-blur-sm transition-all duration-300 ${isLoading ? "opacity-75" : "opacity-100"}`}
      >
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Content */}
            <div className="flex-1">
              <p className="tg-small mb-2 font-bold">
                Questo sito utilizza cookie per migliorare la tua esperienza di
                navigazione.
              </p>
              <p className="tg-small text-gray-300">
                Utilizziamo cookie tecnici necessari per il funzionamento del sito e
                cookie analitici per comprendere come i visitatori interagiscono con il
                nostro sito web. Puoi gestire le tue preferenze o consultare la nostra{" "}
                <Link
                  to="/privacy_policy"
                  className="text-tennis-400 hover:text-tennis-300 underline transition-colors"
                >
                  informativa sulla privacy
                </Link>{" "}
                e{" "}
                <Link
                  to="/cookie_policy"
                  className="text-tennis-400 hover:text-tennis-300 underline transition-colors"
                >
                  cookie policy
                </Link>
                .
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 sm:flex-row md:flex-shrink-0">
              <button
                type="button"
                onClick={handleCustomizePreferences}
                disabled={isLoading}
                className="tg-small rounded-lg border border-gray-600 bg-transparent px-4 py-2 font-bold text-gray-300 transition-colors hover:border-white hover:bg-white/12 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Personalizza
              </button>
              <button
                type="button"
                onClick={handleDeclineAll}
                disabled={isLoading}
                className="tg-small rounded-lg border border-gray-600 bg-transparent px-4 py-2 font-bold text-gray-300 transition-colors hover:border-white hover:bg-white/12 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Rifiuta Non Essenziali
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                disabled={isLoading}
                className="bg-tennis-900 hover:bg-tennis-800 tg-small rounded-lg px-4 py-2 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? "..." : "Accetta Tutti"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Preference Center Modal */}
      <CookiePreferenceCenter
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handlePreferencesSaved}
      />
    </>
  );
}

// Helper functions to check consent status (updated to use new cookie manager)
export const getCookieConsent = (): boolean => {
  return cookieManager.hasConsentBeenSet();
};

export const getAnalyticsConsent = (): boolean => {
  return cookieManager.isCategoryConsented(CookieCategory.ANALYTICS);
};

export const hasConsentBeenSet = (): boolean => {
  return cookieManager.hasConsentBeenSet();
};

// Additional helper functions for the new system
export const getMarketingConsent = (): boolean => {
  return cookieManager.isCategoryConsented(CookieCategory.MARKETING);
};

// Note: Preferences are now handled via localStorage (no cookie consent needed)
export const getPreferencesConsent = (): boolean => {
  return true; // Always available since they're localStorage-only
};

export const getCurrentConsent = () => {
  return cookieManager.getConsent();
};

export const clearAllConsent = () => {
  cookieManager.clearConsent();
};
